import React, { Suspense, lazy } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import SiteHeader from "@/components/common/SiteHeader";
import { useRdpLL88SubmissionQuery } from "@/queries/queries";
import { Button } from "@/components/shared/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/base";
import { toast } from "@/queries/useToast";
import { Loader2, AlertTriangle } from "lucide-react";
import CalendarYearPill from "@/components/common/CalendarYearPill";

// Wave-17 Item 1.1 — RDP LL88 submission page lazy-loaded.
const LL88Page = lazy(() => import("@/pages/RDP/LL88/Submission/Page"));

// Narrow shape of the axios error we care about: backend errors are
// JSON-bodied and include a `message` field. Anything else falls through
// to the static fallback below.
type ConfirmErrorLike = {
  response?: { data?: { message?: string } };
};

const RDP_CONSENT_TOOLTIP =
  "Re-confirm your RDP acceptance for this calendar year to continue.";

export const LL88Route = () => {
  const { submissionId } = useParams({ from: "/rdp/ll88/$submissionId" });
  const { data } = useRdpLL88SubmissionQuery(submissionId);
  const submission = data?.submission ?? data;
  const year: number | undefined = submission?.calendarYear;
  // Mirrors the LL97 route. Backend contract: `rdp_confirmed_for_current_year`
  // (camelized on the client). Strict `=== false` check so an old backend
  // that omits the field does not accidentally gate the page.
  const needsReconsent = submission?.rdpConfirmedForCurrentYear === false;

  const queryClient = useQueryClient();
  const confirmMutation = useMutation({
    // Backend endpoint: `POST /api/audits/ll88/{id}/confirm_rdp_for_year/`.
    mutationFn: () =>
      api.post(`/audits/ll88/${submissionId}/confirm_rdp_for_year/`),
    onSuccess: () => {
      toast({
        title: "Confirmed",
        description: "RDP acceptance recorded for this calendar year.",
      });
      queryClient.invalidateQueries({
        queryKey: ["rdpLL88Submission", submissionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ll88Submission", submissionId],
      });
    },
    onError: (err: ConfirmErrorLike) => {
      toast({
        title: "Error",
        description:
          err?.response?.data?.message ??
          "Failed to record RDP acceptance. Please retry.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex flex-col w-full min-h-full">
      <SiteHeader header="LL88" />
      <div className="flex items-center gap-3 px-10 pt-4">
        <CalendarYearPill year={year} />
      </div>
      {needsReconsent && (
        <div
          role="alert"
          className="mx-10 mt-4 rounded-md border border-yellow-500 bg-yellow-50 p-4 flex items-start gap-3"
        >
          <AlertTriangle
            className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <div className="font-semibold text-yellow-900">
              Action required: Re-confirm your RDP acceptance
              {typeof year === "number" ? ` for CY${year}` : ""}
            </div>
            <div className="text-sm text-yellow-900 mt-1">
              Certification and submit actions are disabled until you confirm.
            </div>
          </div>
          <Button
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending}
          >
            {confirmMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Accept
          </Button>
        </div>
      )}
      <div
        className={needsReconsent ? "relative" : undefined}
        title={needsReconsent ? RDP_CONSENT_TOOLTIP : undefined}
      >
        <div
          aria-disabled={needsReconsent || undefined}
          className={
            needsReconsent ? "pointer-events-none opacity-60" : undefined
          }
        >
          <Suspense fallback={null}>
            <LL88Page />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/rdp/ll88/$submissionId")({
  component: LL88Route,
});
